/** Bilingual (EN/NL) support for everything participants see.
 *
 * Two mechanisms, both tiny (the same pattern as cocoder):
 * - `t(key)` — fixed UI strings from the dictionary below.
 * - `pick(value)` — localized worksheet text from definitions.json:
 *   a field may be a plain string (shown as-is in both languages) or
 *   `{ nl, en }`.
 *
 * The choice is per device (`scapescene:lang`), English by default; NL
 * is one tap away in the header. Switching reloads the page: every view
 * rebuilds from server state anyway, so a reload is simpler and safer
 * than threading re-render hooks through each screen.
 */

export type Lang = "nl" | "en";

export type LocalizedText = string | { nl: string; en: string };

const LANG_KEY = "scapescene:lang";
const THEME_KEY = "scapescene:theme";

export type Theme = "light" | "dark";

/** The look is per device, dark by default (the workshop runs in a
 * dimmed room, and the film reel strip reads best on dark). */
export function getTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage unavailable — fall through to the default.
  }
  return "dark";
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Nothing to persist to; applyTheme still switches it for now.
  }
}

/** Pins the theme on the widget host: starling's /host entry keys its
 * dark tokens off data-theme on :host, and an explicit value wins
 * over the OS preference in both directions. */
export function applyTheme(host: Element, theme: Theme = getTheme()): void {
  host.setAttribute("data-theme", theme);
}

export function getLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === "nl" || stored === "en") return stored;
  } catch {
    // localStorage unavailable — fall through to the default.
  }
  return "en";
}

export function setLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Nothing to persist to; the reload below still applies it for now.
  }
  location.reload();
}

/** Resolves a localized worksheet value for the current language. */
export function pick(value: LocalizedText | undefined): string {
  if (value === undefined) return "";
  if (typeof value === "string") return value;
  return value[getLang()] ?? value.en ?? value.nl ?? "";
}

const STRINGS = {
  // header
  navSetup: { nl: "Opzet", en: "Setup" },
  navFrames: { nl: "Scènes", en: "Scenes" },
  navFilm: { nl: "Film", en: "Film" },
  untitledGroup: { nl: "Groep zonder naam", en: "Untitled group" },
  joinOrCreate: {
    nl: "Doe mee met of maak een andere groep",
    en: "Join or create another group",
  },

  // start screen
  tagline: {
    nl: "Maak samen een korte film — iedereen op z'n eigen telefoon.",
    en: "Make a short film with your group — everyone on their own phone.",
  },
  ssName: { nl: "Je naam", en: "Your name" },
  ssNamePlaceholder: { nl: "bijv. Jamie", en: "e.g. Jamie" },
  ssJoin: { nl: "Doe mee met een bestaande groep", en: "Join an existing group" },
  ssJoinHint: {
    nl: "Vraag de groep om de code van 5 tekens.",
    en: "Ask the group for the 5-character code.",
  },
  ssCode: { nl: "Groepscode", en: "Group code" },
  ssJoinButton: { nl: "Meedoen", en: "Join" },
  ssCreate: { nl: "Begin een nieuwe groep", en: "Create a new group" },
  ssCreateHint: {
    nl: "Begin vers en nodig anderen uit met een code.",
    en: "Start fresh and invite others with a code.",
  },
  ssCreateButton: { nl: "Nieuwe groep", en: "Create" },
  ssYourGroups: { nl: "Je groepen", en: "Your groups" },

  // group screen — shared group card + member list
  ssGroupName: { nl: "Groepsnaam", en: "Group name" },
  ssGroupNamePlaceholder: { nl: "bijv. Tafel 3", en: "e.g. Room 4B" },
  ssCodeLabel: { nl: "Code", en: "Code" },
  ssScanHint: {
    nl: "Scan om op een andere telefoon mee te doen",
    en: "Scan to join this group on another phone",
  },
  ssMembers: { nl: "Wie doet er mee?", en: "Who is here" },
  ssAddPlaceholder: { nl: "Voeg een naam toe", en: "Add a name" },
  ssAddButton: { nl: "Toevoegen", en: "Add" },
  ssAddHint: {
    nl: "Voor wie geen eigen telefoon heeft — hun naam rolt gewoon mee in de aftiteling.",
    en: "For people without their own phone — their name still rolls in the end credits.",
  },
  ssYou: { nl: "(jij)", en: "(you)" },
  ssRemoveConfirm: {
    nl: "Deze persoon uit de groep verwijderen?",
    en: "Remove this person from the group?",
  },

  // group screen — onboarding
  howTitle: { nl: "Zo werkt het", en: "How this works" },
  howIntro: {
    nl: "Samen maken jullie een korte film die één verhaal vertelt in {n} scènes. Eén scène = één moment. Het verhaal bouwt op — het grote moment komt aan het eind, niet aan het begin. Een film over het winnen van de beker? De beker komt in de láátste scène. Scène 1 is een gewone trainingsdag, voordat er iets gebeurde.",
    en: "Together you'll make a short film that tells one story in {n} scenes. One scene = one moment. The story builds up — the big moment comes near the end, not at the start. Making a film about winning the cup? The cup comes in the last scene. Scene 1 is an ordinary training day, before anything happened.",
  },
  projectTitle: { nl: "Titel van je film", en: "Project title" },
  untitledFilm: { nl: "Film zonder titel", en: "Untitled film" },
  saved: { nl: "Opgeslagen", en: "Saved" },
  changeLabel: {
    nl: "Wat verandert er in jullie verhaal?",
    en: "What changes in your story?",
  },
  changeHint: {
    nl: "Elk verhaal is een ervoor en een erna. Schrijf allebei op: 'Aan het begin … — aan het eind …'",
    en: "Every story is a before and an after. Write both: 'At the start … — at the end …'",
  },
  changePlaceholder: {
    nl: "Aan het begin heeft de club nog nooit iets gewonnen — aan het eind tillen ze de beker op.",
    en: "At the start the club has never won anything — at the end they lift the cup.",
  },
  startStoryboarding: { nl: "Begin met storyboarden", en: "Start storyboarding" },
  couldNotSave: { nl: "Opslaan lukte niet.", en: "Could not save changes." },

  // frame editor
  frameWord: { nl: "Scène", en: "Scene" },
  moreHelp: { nl: "Meer uitleg", en: "More help" },
  uploading: { nl: "Uploaden…", en: "Uploading…" },
  sketchAlt: { nl: "Schets van deze scène", en: "Sketch of this scene" },
  sketchLabel: {
    nl: "Stap 1 — foto van je schets",
    en: "Step 1 — photo of your sketch",
  },
  sketchTitle: {
    nl: "Fotografeer je schets, of upload een foto",
    en: "Photograph your sketch, or upload a photo",
  },
  takePhoto: { nl: "Maak een foto", en: "Take a photo" },
  uploadPhoto: { nl: "Upload een foto", en: "Upload a photo" },
  cameraShutter: { nl: "Foto maken", en: "Take photo" },
  cameraCancel: { nl: "Annuleren", en: "Cancel" },
  cameraUnavailable: {
    nl: "Geen camera gevonden — upload een foto in plaats daarvan.",
    en: "No camera found — upload a photo instead.",
  },
  sketchWhy: {
    nl: "De schets bepaalt de compositie van het eerste beeld van deze scène.",
    en: "The sketch sets the layout of this scene's first frame.",
  },
  sketchDone: {
    nl: "Schets staat erin — maak of upload een andere om te vervangen.",
    en: "Sketch is in — take or upload another one to replace it.",
  },
  sketchReplaceHint: {
    nl: "Dit is het eerste beeld van de scène. Maak of upload een nieuwe schets om opnieuw te beginnen.",
    en: "This is the scene's first frame. Take or upload a new sketch to start over.",
  },
  fieldsLabel: {
    nl: "Stap 2 — beschrijf de scène",
    en: "Step 2 — describe the scene",
  },
  howItWorksFrame: {
    nl: "Fotografeer je schets, beschrijf de scène en druk op \"Maak eerste beeld\". Uit dat eerste beeld wordt de video van deze scène automatisch gemaakt.",
    en: "Photograph your sketch, describe the scene and press \"Create first frame\". The scene's video is then made automatically from that first frame.",
  },
  sketchUploadFailed: {
    nl: "De schets uploaden lukte niet.",
    en: "Couldn't upload the sketch.",
  },
  saveDraft: { nl: "Bewaar concept", en: "Save draft" },
  notSavedPrefix: { nl: "Niet opgeslagen:", en: "Not saved:" },
  saving: { nl: "Opslaan…", en: "Saving…" },
  unsavedChanges: { nl: "Niet-opgeslagen wijzigingen", en: "Unsaved changes" },
  makePicture: { nl: "Maak eerste beeld", en: "Create first frame" },
  makePictureHint: {
    nl: "Uit het eerste beeld wordt de video van deze scène automatisch gemaakt.",
    en: "The scene's video is created automatically from the first frame.",
  },
  makeClip: { nl: "Maak video", en: "Make video" },
  redoClip: { nl: "Video opnieuw", en: "Redo video" },
  newPicture: { nl: "Nieuw eerste beeld", en: "New first frame" },
  regenHint: {
    nl: "Opnieuw maken telt mee: {n} over voor deze scène.",
    en: "Redoing counts: {n} left for this scene.",
  },
  rendering: { nl: "Bezig…", en: "Working…" },
  makingClip: {
    nl: "De video van deze scène wordt gemaakt — dit duurt een paar minuten.",
    en: "Making this scene's video — this takes a few minutes.",
  },
  makingPicture: {
    nl: "Het eerste beeld wordt gemaakt, daarna volgt de video vanzelf — dit duurt een paar minuten.",
    en: "Creating the first frame; the video follows on its own — this takes a few minutes.",
  },
  videoNotEnabled: {
    nl: "Videogeneratie staat niet aan op deze server.",
    en: "Video generation is not enabled on this server.",
  },
  makePreviousFirst: {
    nl: "Maak eerst het eerste beeld van scène {n} — elke scène leent haar look van de vorige.",
    en: "Create scene {n}'s first frame first — each scene borrows its look from the one before.",
  },
  frameCapReached: {
    nl: "Deze scène heeft alle {n} keer opnieuw maken al gebruikt.",
    en: "This scene has used all {n} of its redos.",
  },
  noGenerationsLeft: {
    nl: "De groep kan niets meer opnieuw maken.",
    en: "The group has no redos left.",
  },
  renderFailed: {
    nl: "Genereren mislukt — probeer het nog eens.",
    en: "Rendering failed — try again.",
  },

  // film screen
  theFilm: { nl: "De film", en: "The film" },
  groupFallback: { nl: "Groep", en: "Group" },
  theRenders: { nl: "De beelden", en: "The renders" },
  introCard: { nl: "Titelkaart", en: "Intro card" },
  creditsCard: { nl: "Aftiteling", en: "Credits card" },
  clipWord: { nl: "video", en: "video" },
  pictureWord: { nl: "eerste beeld", en: "first frame" },
  frameRenderAlt: { nl: "Beeld van scène {n}", en: "Scene {n} render" },
  videoNotEnabledWorkshop: {
    nl: "Videogeneratie staat niet aan voor deze workshop.",
    en: "Video generation is not enabled for this workshop.",
  },
  filmFailed: {
    nl: "Er ging iets mis bij het maken van de film.",
    en: "Something went wrong rendering the film.",
  },
  noFilmYet: {
    nl: "Nog geen film — zodra alle scènes een video hebben worden ze vanzelf samengevoegd, of doe het hieronder.",
    en: "No film yet — once every scene has its video they are combined on their own, or do it below.",
  },
  downloadFilm: { nl: "Download de film", en: "Download film" },
  subtitles: { nl: "Ondertitels", en: "Subtitles" },
  watchFullScreen: { nl: "Kijk beeldvullend", en: "Watch full screen" },
  renderFilm: {
    nl: "Voeg video's samen + ondertitels",
    en: "Combine videos + captions",
  },
  reRenderFilm: {
    nl: "Opnieuw samenvoegen + ondertitels",
    en: "Combine again + captions",
  },
  combineHint: {
    nl: "Plakt de video's van alle scènes achter elkaar, schrijft een vertelstem en brandt de ondertitels erin.",
    en: "Joins every scene's video, writes the narration and burns in the captions.",
  },
  videoNotEnabledShort: {
    nl: "Videogeneratie staat niet aan",
    en: "Video generation is not enabled",
  },
  renderOneFrameFirst: {
    nl: "Maak eerst minstens één scènevideo",
    en: "Make at least one scene video first",
  },
  renderingFilmShort: {
    nl: "Video's worden samengevoegd…",
    en: "Combining the videos…",
  },
  couldNotRenderFilm: {
    nl: "Samenvoegen lukte niet.",
    en: "Could not combine the videos.",
  },
  renderingFilm: {
    nl: "Video's worden samengevoegd en ondertiteld… dit kan een paar minuten duren.",
    en: "Combining the videos and adding captions… this can take a few minutes.",
  },

  // final screen
  backToStoryboard: {
    nl: "Terug naar het storyboard",
    en: "Back to the storyboard",
  },
  noFilmYetFinal: {
    nl: "Nog geen film — zodra alle scènes een video hebben worden ze vanzelf samengevoegd.",
    en: "No film yet — once every scene has its video they are combined on their own.",
  },

  // theme
  themeToggle: { nl: "Licht / donker", en: "Light / dark" },
} satisfies Record<string, { nl: string; en: string }>;

export type StringKey = keyof typeof STRINGS;

export function t(key: StringKey): string {
  return STRINGS[key][getLang()];
}

/** t() plus `{n}` interpolation, for the handful of counted strings. */
export function tn(key: StringKey, n: number | string): string {
  return t(key).replace("{n}", String(n));
}
